import { useEffect, useMemo, useState } from 'react';
import { DECKS } from '../../config/decks';
import { extractHeadings, Markdown } from '../../components/Markdown';
import type { DeckId } from '../../types';

export function SummariesView() {
  const [deckId, setDeckId] = useState<DeckId>('bs');
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [activeId, setActiveId] = useState('');
  const deck = DECKS.find((item) => item.id === deckId)!;
  const toc = useMemo(() => extractHeadings(markdown, 4), [markdown]);

  useEffect(() => {
    setError(null);
    setMarkdown('');
    setActiveId('');
    fetch(deck.summary)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.text();
      })
      .then(setMarkdown)
      .catch((err) => setError(err instanceof Error ? err.message : 'Zusammenfassung konnte nicht geladen werden.'));
  }, [deck.summary]);

  useEffect(() => {
    if (!toc.length) return;
    setActiveId(toc[0].id);
    const observer = new IntersectionObserver((entries) => {
      const visible = entries
        .filter((entry) => entry.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (visible?.target.id) setActiveId(visible.target.id);
    }, { rootMargin: '-20% 0px -70% 0px', threshold: 0.01 });
    for (const heading of toc) {
      const node = document.getElementById(heading.id);
      if (node) observer.observe(node);
    }
    return () => observer.disconnect();
  }, [markdown, toc]);

  return (
    <section className="view view-summaries active">
      <header className="page-head">
        <div><h1>Zusammenfassungen</h1><p className="muted">Navigierbarer Markdown-Viewer mit Inhaltsverzeichnis, Ankern und Druckansicht.</p></div>
        <div className="head-actions">
          <label className="select-wrap"><span className="sr-only">Fach wählen</span><select className="select" value={deckId} onChange={(event) => setDeckId(event.target.value as DeckId)}>{DECKS.map((item) => <option key={item.id} value={item.id}>{item.icon}  {item.name}</option>)}</select></label>
          <button className="btn-ghost" onClick={() => window.print()}>Drucken</button>
        </div>
      </header>
      {error && <div className="toast error">{error}</div>}
      <div className="summary-layout">
        <aside className="summary-toc" aria-label="Inhaltsverzeichnis">
          <div className="toc-title">{deck.icon} {deck.name}</div>
          {toc.length === 0 ? <p className="muted">Inhalt wird geladen…</p> : toc.map((heading) => (
            <a
              key={heading.id}
              className={`toc-l${heading.level} ${activeId === heading.id ? 'active' : ''}`}
              href={`#${heading.id}`}
              onClick={(event) => {
                const target = document.getElementById(heading.id);
                if (!target) return;
                event.preventDefault();
                target.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setActiveId(heading.id);
                history.replaceState(null, '', `#${heading.id}`);
              }}
            >{heading.text}</a>
          ))}
        </aside>
        <article className="summary-content">
          <Markdown text={markdown || 'Lade Zusammenfassung…'} className="markdown summary-doc" withHeadingIds />
        </article>
      </div>
    </section>
  );
}
